import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageSquare, ArrowLeft, Save, Zap, Globe, Eye, EyeOff, Send, Smartphone, Inbox, RefreshCw, Plus, Trash2, Copy } from 'lucide-react';
import { clearWhatsAppConfigCache } from '@/utils/whatsappHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuth = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
};

export default function WhatsAppSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [showToken, setShowToken] = React.useState(false);
  const [testPhone, setTestPhone] = React.useState('');
  const [providers, setProviders] = React.useState([]);
  const [activeTab, setActiveTab] = React.useState('outbound');
  const [webhookPresets, setWebhookPresets] = React.useState({});
  const [inboxConfig, setInboxConfig] = React.useState({
    inbox_enabled: false,
    webhook_key: '',
    webhook_secret: '',
    inbox_provider: '',
    field_mapping: { phone: '', message: '', name: '', timestamp: '', message_id: '' },
    reply_templates: [],
    auto_create_lead: false,
    default_lead_source: 'WhatsApp',
    default_lead_stage: 'new',
    default_lead_priority: 'warm',
  });
  const [newTemplate, setNewTemplate] = React.useState('');

  const [config, setConfig] = React.useState({
    mode: 'wame',
    provider: '',
    api_endpoint: '',
    api_token: '',
    auth_header_name: 'Authorization',
    auth_header_format: 'Basic {token}',
    request_body_template: '',
    http_method: 'POST',
  });

  React.useEffect(() => {
    fetchConfig();
    fetchProviders();
    fetchInboxConfig();
    fetchWebhookPresets();
  }, []);

  const fetchConfig = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/config`, getAuth());
      setConfig(prev => ({ ...prev, ...resp.data }));
    } catch (e) {
      console.error('Failed to fetch config');
    }
    setLoading(false);
  };

  const fetchProviders = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/providers`, getAuth());
      setProviders(resp.data.providers || []);
    } catch (e) {
      console.error('Failed to fetch providers');
    }
  };

  const fetchInboxConfig = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/inbox-config`, getAuth());
      setInboxConfig(prev => ({ ...prev, ...resp.data }));
    } catch { /* silent */ }
  };

  const fetchWebhookPresets = async () => {
    try {
      const resp = await axios.get(`${BACKEND_URL}/api/whatsapp/webhook-presets`, getAuth());
      setWebhookPresets(resp.data.presets || {});
    } catch { /* silent */ }
  };

  const handleInboxProviderChange = (provider) => {
    setInboxConfig(prev => ({ ...prev, inbox_provider: provider }));
    const preset = webhookPresets[provider];
    if (preset) {
      setInboxConfig(prev => ({ ...prev, inbox_provider: provider, field_mapping: { ...preset } }));
    }
  };

  const handleSaveInbox = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/inbox-config`, inboxConfig, getAuth());
      toast.success('Inbox configuration saved!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleRegenerateKey = async () => {
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/whatsapp/inbox-config/regenerate-key`, {}, getAuth());
      setInboxConfig(prev => ({ ...prev, webhook_key: resp.data.webhook_key }));
      toast.success('Webhook key regenerated');
    } catch { toast.error('Failed'); }
  };

  const copyWebhookUrl = () => {
    const url = `${BACKEND_URL}/api/whatsapp/webhook/${inboxConfig.webhook_key}`;
    navigator.clipboard.writeText(url);
    toast.success('Webhook URL copied!');
  };

  const handleProviderChange = (providerId) => {
    setConfig(prev => ({ ...prev, provider: providerId }));
    if (providerId && providerId !== 'custom') {
      const preset = providers.find(p => p.id === providerId);
      if (preset) {
        setConfig(prev => ({
          ...prev,
          provider: providerId,
          api_endpoint: preset.api_endpoint || '',
          auth_header_name: preset.auth_header_name || 'Authorization',
          auth_header_format: preset.auth_header_format || 'Basic {token}',
          request_body_template: preset.request_body_template || '',
          http_method: preset.http_method || 'POST',
        }));
      }
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/whatsapp/config`, config, getAuth());
      clearWhatsAppConfigCache();
      toast.success('WhatsApp configuration saved!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleTest = async () => {
    if (!testPhone) { toast.error('Enter a test phone number'); return; }
    setTesting(true);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/whatsapp/test`, { phone: testPhone }, getAuth());
      if (resp.data.status === 'sent') {
        toast.success('Test message sent successfully!');
      } else {
        toast.error(`Test failed: ${resp.data.error || 'Unknown error'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    }
    setTesting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/modules')} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="wa-back-btn">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-green-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-800">WhatsApp Settings</h1>
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 font-medium" data-testid="wa-save-btn">
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Tab Switcher */}
        <div className="flex gap-2 bg-white rounded-xl border p-1.5">
          <button onClick={() => setActiveTab('outbound')} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'outbound' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-testid="wa-tab-outbound">
            Outbound (Sending)
          </button>
          <button onClick={() => setActiveTab('inbox')} className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === 'inbox' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`} data-testid="wa-tab-inbox">
            Inbox (Receiving)
          </button>
        </div>

        {activeTab === 'outbound' && (<>
        {/* Delivery Mode */}
        <div className="bg-white rounded-xl border p-6" data-testid="wa-mode-section">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Delivery Mode</h2>
          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => setConfig(prev => ({ ...prev, mode: 'wame' }))}
              className={`p-4 rounded-xl border-2 text-left transition-all ${config.mode === 'wame' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid="wa-mode-wame"
            >
              <div className="flex items-center gap-3 mb-2">
                <Smartphone className={`w-6 h-6 ${config.mode === 'wame' ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-semibold text-gray-800">wa.me Link</span>
              </div>
              <p className="text-sm text-gray-500">Opens native WhatsApp app on mobile. Best for phones & tablets. Works on desktop too.</p>
            </button>
            <button
              onClick={() => setConfig(prev => ({ ...prev, mode: 'browser' }))}
              className={`p-4 rounded-xl border-2 text-left transition-all ${config.mode === 'browser' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid="wa-mode-browser"
            >
              <div className="flex items-center gap-3 mb-2">
                <Globe className={`w-6 h-6 ${config.mode === 'browser' ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-semibold text-gray-800">WhatsApp Web</span>
              </div>
              <p className="text-sm text-gray-500">Opens WhatsApp Web directly. Best for desktop. Requires network access to web.whatsapp.com.</p>
            </button>
            <button
              onClick={() => setConfig(prev => ({ ...prev, mode: 'api' }))}
              className={`p-4 rounded-xl border-2 text-left transition-all ${config.mode === 'api' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid="wa-mode-api"
            >
              <div className="flex items-center gap-3 mb-2">
                <Zap className={`w-6 h-6 ${config.mode === 'api' ? 'text-green-600' : 'text-gray-400'}`} />
                <span className="font-semibold text-gray-800">API Provider</span>
              </div>
              <p className="text-sm text-gray-500">Server-side via Interakt/Twilio/etc. Works everywhere, even if WhatsApp is blocked.</p>
            </button>
          </div>
        </div>

        {/* API Configuration - shown only in API mode */}
        {config.mode === 'api' && (
          <>
            {/* Provider Selection */}
            <div className="bg-white rounded-xl border p-6" data-testid="wa-provider-section">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">API Provider</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {providers.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleProviderChange(p.id)}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${config.provider === p.id ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid={`wa-provider-${p.id}`}
                  >
                    {p.name}
                  </button>
                ))}
                <button
                  onClick={() => handleProviderChange('custom')}
                  className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${config.provider === 'custom' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  data-testid="wa-provider-custom"
                >
                  Custom
                </button>
              </div>
            </div>

            {/* API Details */}
            <div className="bg-white rounded-xl border p-6 space-y-4" data-testid="wa-api-section">
              <h2 className="text-lg font-semibold text-gray-800">API Configuration</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint URL</label>
                <input
                  type="url"
                  value={config.api_endpoint}
                  onChange={e => setConfig(prev => ({ ...prev, api_endpoint: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="https://api.provider.com/v1/message/"
                  data-testid="wa-api-endpoint"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key / Token</label>
                <div className="relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={config.api_token}
                    onChange={e => setConfig(prev => ({ ...prev, api_token: e.target.value }))}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="Paste your API key here"
                    data-testid="wa-api-token"
                  />
                  <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Header Name</label>
                  <input
                    type="text"
                    value={config.auth_header_name}
                    onChange={e => setConfig(prev => ({ ...prev, auth_header_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Authorization"
                    data-testid="wa-auth-header-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Header Format</label>
                  <input
                    type="text"
                    value={config.auth_header_format}
                    onChange={e => setConfig(prev => ({ ...prev, auth_header_format: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="Basic {token}"
                    data-testid="wa-auth-header-format"
                  />
                  <p className="text-xs text-gray-400 mt-1">Use <code className="bg-gray-100 px-1 rounded">{'{token}'}</code> as placeholder for your API key</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Request Body Template (JSON)</label>
                <textarea
                  value={config.request_body_template}
                  onChange={e => setConfig(prev => ({ ...prev, request_body_template: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                  rows={8}
                  placeholder='{"phoneNumber": "{phone}", "type": "Text", "data": {"message": "{message}"}}'
                  data-testid="wa-body-template"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use <code className="bg-gray-100 px-1 rounded">{'{phone}'}</code> for phone number and <code className="bg-gray-100 px-1 rounded">{'{message}'}</code> for message text
                </p>
              </div>
            </div>

            {/* Test Connection */}
            <div className="bg-white rounded-xl border p-6" data-testid="wa-test-section">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Test Connection</h2>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Test Phone Number</label>
                  <input
                    type="text"
                    value={testPhone}
                    onChange={e => setTestPhone(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="919876543210"
                    data-testid="wa-test-phone"
                  />
                </div>
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium whitespace-nowrap"
                  data-testid="wa-test-btn"
                >
                  <Send className="w-4 h-4" />
                  {testing ? 'Sending...' : 'Send Test'}
                </button>
              </div>
              {config.last_tested_at && (
                <div className={`mt-3 text-sm flex items-center gap-2 ${config.last_test_status === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                  <span className={`w-2 h-2 rounded-full ${config.last_test_status === 'success' ? 'bg-green-500' : 'bg-red-500'}`} />
                  Last tested: {new Date(config.last_tested_at).toLocaleString()} — {config.last_test_status === 'success' ? 'Connected' : 'Failed'}
                </div>
              )}
            </div>
          </>
        )}

        {/* Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
          <strong>Note:</strong> <strong>wa.me Link</strong> is recommended for mobile users — it opens the native WhatsApp app directly. <strong>WhatsApp Web</strong> is best for desktop users with network access. <strong>API Provider</strong> sends messages server-side and works everywhere, even if WhatsApp is blocked on your network.</div>
        </>)}

        {/* ═══════ INBOX TAB ═══════ */}
        {activeTab === 'inbox' && (<>
          {/* Enable Toggle */}
          <div className="bg-white rounded-xl border p-6" data-testid="inbox-enable-section">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">WhatsApp Inbox</h2>
                <p className="text-sm text-gray-500 mt-1">Receive incoming WhatsApp messages and create leads from them</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={inboxConfig.inbox_enabled} onChange={e => setInboxConfig(prev => ({ ...prev, inbox_enabled: e.target.checked }))} className="sr-only peer" data-testid="inbox-enable-toggle" />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              </label>
            </div>
          </div>

          {inboxConfig.inbox_enabled && (<>
            {/* Webhook URL */}
            <div className="bg-white rounded-xl border p-6" data-testid="inbox-webhook-section">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Webhook Configuration</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL <span className="text-gray-400">(paste this in your provider's webhook settings)</span></label>
                  <div className="flex gap-2">
                    <input type="text" readOnly value={`${BACKEND_URL}/api/whatsapp/webhook/${inboxConfig.webhook_key}`} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50" data-testid="inbox-webhook-url" />
                    <button onClick={copyWebhookUrl} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg" title="Copy"><Copy className="w-4 h-4 text-gray-600" /></button>
                    <button onClick={handleRegenerateKey} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg" title="Regenerate"><RefreshCw className="w-4 h-4 text-gray-600" /></button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook Secret <span className="text-gray-400">(optional — for signature verification)</span></label>
                  <input type="text" value={inboxConfig.webhook_secret} onChange={e => setInboxConfig(prev => ({ ...prev, webhook_secret: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" placeholder="Enter webhook secret from your provider" data-testid="inbox-webhook-secret" />
                </div>
              </div>
            </div>

            {/* Provider Field Mapping */}
            <div className="bg-white rounded-xl border p-6" data-testid="inbox-mapping-section">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Provider & Field Mapping</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                {Object.keys(webhookPresets).map(key => (
                  <button key={key} onClick={() => handleInboxProviderChange(key)}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium capitalize transition-all ${inboxConfig.inbox_provider === key ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                    data-testid={`inbox-provider-${key}`}>
                    {key === 'meta_direct' ? 'Meta Direct' : key.charAt(0).toUpperCase() + key.slice(1)}
                  </button>
                ))}
                <button onClick={() => setInboxConfig(prev => ({ ...prev, inbox_provider: 'custom' }))}
                  className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-all ${inboxConfig.inbox_provider === 'custom' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                  data-testid="inbox-provider-custom">Custom</button>
              </div>
              <p className="text-xs text-gray-400 mb-3">Map JSON field paths from your provider's webhook payload. Use dot notation (e.g., <code className="bg-gray-100 px-1 rounded">data.customer.phone</code>).</p>
              <div className="grid grid-cols-2 gap-3">
                {['phone', 'message', 'name', 'timestamp', 'message_id'].map(field => (
                  <div key={field}>
                    <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">{field.replace('_', ' ')} Path {field === 'phone' || field === 'message' ? '*' : ''}</label>
                    <input type="text" value={inboxConfig.field_mapping[field] || ''} onChange={e => setInboxConfig(prev => ({ ...prev, field_mapping: { ...prev.field_mapping, [field]: e.target.value } }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" placeholder={`e.g., data.${field}`} data-testid={`inbox-field-${field}`} />
                  </div>
                ))}
              </div>
            </div>

            {/* Auto Lead + Defaults */}
            <div className="bg-white rounded-xl border p-6" data-testid="inbox-auto-lead-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Auto-Create Leads</h2>
                  <p className="text-sm text-gray-500">Automatically create a lead when a new WhatsApp message arrives</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={inboxConfig.auto_create_lead} onChange={e => setInboxConfig(prev => ({ ...prev, auto_create_lead: e.target.checked }))} className="sr-only peer" data-testid="inbox-auto-lead-toggle" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Default Lead Source</label>
                  <input type="text" value={inboxConfig.default_lead_source} onChange={e => setInboxConfig(prev => ({ ...prev, default_lead_source: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="inbox-default-source" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Default Stage</label>
                  <select value={inboxConfig.default_lead_stage} onChange={e => setInboxConfig(prev => ({ ...prev, default_lead_stage: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="new">New Lead</option>
                    <option value="contacted">Contacted</option>
                    <option value="interested">Interested</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Default Priority</label>
                  <select value={inboxConfig.default_lead_priority} onChange={e => setInboxConfig(prev => ({ ...prev, default_lead_priority: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="hot">Hot</option>
                    <option value="warm">Warm</option>
                    <option value="cold">Cold</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Reply Templates */}
            <div className="bg-white rounded-xl border p-6" data-testid="inbox-templates-section">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Quick Reply Templates</h2>
              <div className="space-y-2 mb-3">
                {inboxConfig.reply_templates.map((tpl, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <span className="text-sm text-gray-700 flex-1">{tpl}</span>
                    <button onClick={() => setInboxConfig(prev => ({ ...prev, reply_templates: prev.reply_templates.filter((_, idx) => idx !== i) }))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input type="text" value={newTemplate} onChange={e => setNewTemplate(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Add a new quick reply template..." data-testid="inbox-new-template" />
                <button onClick={() => { if (newTemplate.trim()) { setInboxConfig(prev => ({ ...prev, reply_templates: [...prev.reply_templates, newTemplate.trim()] })); setNewTemplate(''); } }} className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"><Plus className="w-4 h-4" /></button>
              </div>
            </div>

            {/* Save Inbox Config */}
            <button onClick={handleSaveInbox} disabled={saving} className="w-full py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 font-semibold flex items-center justify-center gap-2" data-testid="inbox-save-btn">
              <Save className="w-5 h-5" /> {saving ? 'Saving...' : 'Save Inbox Configuration'}
            </button>

            {/* Open Inbox Button */}
            <button onClick={() => navigate('/whatsapp/inbox')} className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold flex items-center justify-center gap-2" data-testid="inbox-open-btn">
              <Inbox className="w-5 h-5" /> Open WhatsApp Inbox
            </button>
          </>)}
        </>)}
      </div>
    </div>
  );
}
